import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { CopilotFAB } from "../components/copilot/copilot-fab";
import { CelebrationListener } from "../components/makai/celebration";
import { OfflineProvider } from "../components/offline/offline-provider";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "MAKAI — AI-powered learning for Uganda" },
      { name: "description", content: "Lessons, quizzes, a digital library and the Learning Tree for learners, parents, teachers and schools across Uganda." },
      { name: "author", content: "MAKAI" },
      { property: "og:title", content: "MAKAI — AI-powered learning for Uganda" },
      { property: "og:description", content: "Lessons, quizzes, a digital library and the Learning Tree for learners, parents, teachers and schools across Uganda." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "MAKAI — AI-powered learning for Uganda" },
      { name: "twitter:description", content: "Lessons, quizzes, a digital library and the Learning Tree for learners, parents, teachers and schools across Uganda." },
      { property: "og:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/8UR0xbQynGZKfKZ6VHYDPhwGTao2/social-images/social-1784103838069-1005250920.webp" },
      { name: "twitter:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/8UR0xbQynGZKfKZ6VHYDPhwGTao2/social-images/social-1784103838069-1005250920.webp" },
    ],
    links: [
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Sora:wght@500;600;700;800&display=swap" },
      {
        rel: "stylesheet",
        href: appCss,
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const router = useRouter();

  useEffect(() => {
    // Wire the Learning Events pipeline transport once per session.
    void import("../lib/learning-events.functions").then((m) => m.wireLearningEventTransport());
    const onHide = () => {
      void import("../lib/learning-events").then((m) => m.flushLearningEvents());
    };
    window.addEventListener("pagehide", onHide);
    window.addEventListener("beforeunload", onHide);
    return () => {
      window.removeEventListener("pagehide", onHide);
      window.removeEventListener("beforeunload", onHide);
    };
  }, []);

  // Performance instrumentation: web vitals, API latency and navigation timings.
  useEffect(() => {
    let unsubLoad: (() => void) | undefined;
    let unsubResolved: (() => void) | undefined;
    let startedAt = 0;
    void import("../lib/performance-monitor").then((perf) => {
      perf.startPerformanceMonitor();
      unsubLoad = router.subscribe("onBeforeLoad", () => {
        startedAt = performance.now();
      });
      unsubResolved = router.subscribe("onResolved", ({ toLocation }) => {
        if (!startedAt) return;
        perf.recordRouteSample(toLocation.pathname, Math.round(performance.now() - startedAt));
        startedAt = 0;
      });
    });
    return () => {
      unsubLoad?.();
      unsubResolved?.();
    };
  }, [router]);

  return (
    <QueryClientProvider client={queryClient}>
      {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
      <Outlet />
      <CopilotFAB />
      <CelebrationListener />
      <OfflineProvider />
    </QueryClientProvider>
  );
}

